import { useState } from 'react'
import type {
  CoverageLevel,
  MarketPersona,
  MarketSegmentWithScore,
  SegmentPersonaLink,
  UpdateSegmentInput,
} from '@ispot/shared'
import { SegmentSizingSection } from './SegmentSizingSection'
import { SegmentScoringSection } from './SegmentScoringSection'
import { SegmentPersonasSection } from './SegmentPersonasSection'
import { SegmentBriefSection } from './SegmentBriefSection'
import { EditableSegmentHeader } from './EditableSegmentHeader'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Props {
  segment: MarketSegmentWithScore
  isBeachhead: boolean
  personas: MarketPersona[]
  coverage: SegmentPersonaLink[]
  previousBeachheadName: string | null
  fullScreen?: boolean
  onUpdateSegment: (updates: UpdateSegmentInput) => Promise<void>
  onUpdateField: (path: 'sizing' | 'scoring', key: string, value: number | null) => Promise<void>
  onResetField: (path: 'sizing' | 'scoring', key: string) => Promise<void>
  onLinkPersona: (personaId: string, coverage: CoverageLevel) => Promise<void>
  onUnlinkPersona: (personaId: string) => Promise<void>
  onSetBeachhead: (segmentId: string | null) => Promise<void>
  onDelete: () => Promise<void>
  onToggleFullScreen?: () => void
  onGenerateScoring?: () => Promise<void>
  onGenerateSizing?: () => Promise<void>
  onGenerateBrief?: () => Promise<import('@ispot/shared').MarketBrief>
  onListBriefs?: () => Promise<import('@ispot/shared').MarketBrief[]>
}

export function SegmentDetail({
  segment,
  isBeachhead,
  personas,
  coverage,
  previousBeachheadName,
  fullScreen,
  onUpdateSegment,
  onUpdateField,
  onResetField,
  onLinkPersona,
  onUnlinkPersona,
  onSetBeachhead,
  onDelete,
  onToggleFullScreen,
  onGenerateScoring,
  onGenerateSizing,
  onGenerateBrief,
  onListBriefs,
}: Props) {
  const [confirmBeachhead, setConfirmBeachhead] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [aiAllRunning, setAiAllRunning] = useState(false)

  const canAIAll = !!(onGenerateSizing && onGenerateScoring && onGenerateBrief)
  const handleAIAll = async () => {
    if (!canAIAll) return
    setAiAllRunning(true)
    try {
      // Run in parallel — each is independent (sizing/scoring/brief don't depend on each other).
      await Promise.all([
        onGenerateSizing!().catch(() => {}),
        onGenerateScoring!().catch(() => {}),
        onGenerateBrief!().catch(() => {}),
      ])
    } finally {
      setAiAllRunning(false)
    }
  }

  const handleToggleBeachhead = async () => {
    if (isBeachhead) {
      await onSetBeachhead(null)
      return
    }
    if (previousBeachheadName) {
      setConfirmBeachhead(true)
      return
    }
    await onSetBeachhead(segment.id)
  }

  const handleSuggestDescription = onGenerateBrief
    ? async () => {
        try {
          const brief = await onGenerateBrief()
          // The brief's `definition` is the closest equivalent to a one-line
          // description: "who they are and what they want." Fall back to
          // `summary` if definition is empty.
          const definition = brief.content?.definition?.trim() ?? ''
          const summary = brief.content?.summary?.trim() ?? ''
          const candidate = definition || summary
          if (!candidate) return null
          // Use only the first paragraph; descriptions are short.
          return candidate.split(/\n\s*\n/, 1)[0]?.trim() || null
        } catch {
          return null
        }
      }
    : undefined

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden">
      <EditableSegmentHeader
        segment={segment}
        isBeachhead={isBeachhead}
        fullScreen={fullScreen}
        canAIAll={canAIAll}
        aiAllRunning={aiAllRunning}
        onAIAll={handleAIAll}
        onUpdateSegment={onUpdateSegment}
        onSuggestDescription={handleSuggestDescription}
        onToggleBeachhead={handleToggleBeachhead}
        onDelete={() => setConfirmDelete(true)}
        onToggleFullScreen={onToggleFullScreen}
      />

      <div className="flex-1 overflow-y-auto px-8 py-8">
        <div className="mx-auto max-w-[1280px] space-y-12">
          {/* Numbers: sizing + scoring side-by-side on wide screens (≥1024px).
              Editorial two-column: Market (left) / Fit (right), separated by a hairline. */}
          <div className="grid gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)]">
            <SegmentSizingSection
              segment={segment}
              onUpdateField={(key, value) => onUpdateField('sizing', key, value)}
              onResetField={(key) => onResetField('sizing', key)}
              onGenerateAI={onGenerateSizing}
            />
            <SegmentScoringSection
              segment={segment}
              onUpdateField={(key, value) => onUpdateField('scoring', key, value)}
              onResetField={(key) => onResetField('scoring', key)}
              onGenerateAI={onGenerateScoring}
            />
          </div>

          <hr style={{ borderColor: 'var(--color-border-subtle)' }} />

          <SegmentPersonasSection
            segment={segment}
            personas={personas}
            coverage={coverage}
            onLink={onLinkPersona}
            onUnlink={onUnlinkPersona}
          />

          {onGenerateBrief && onListBriefs ? (
            <>
              <hr style={{ borderColor: 'var(--color-border-subtle)' }} />
              <SegmentBriefSection
                segmentId={segment.id}
                onGenerate={onGenerateBrief}
                onList={onListBriefs}
              />
            </>
          ) : null}
        </div>
      </div>

      <AlertDialog open={confirmBeachhead} onOpenChange={setConfirmBeachhead}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the current beachhead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace{' '}
              <strong>{previousBeachheadName ?? 'the current beachhead'}</strong> with{' '}
              <strong>{segment.name}</strong>. Beachhead designation appears across all views.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setConfirmBeachhead(false)
                await onSetBeachhead(segment.id)
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this segment?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{segment.name}</strong> and all of its brief versions, persona links, and
              scoring/sizing data will be deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setConfirmDelete(false)
                await onDelete()
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
